import React from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Users, Scale, Landmark } from "lucide-react";

const AboutSection = () => {
  const objectives = [
    {
      icon: <Users size={24} />,
      title: "Discovery",
      desc: "Helping startups and MSMEs find the right subject-matter experts effortlessly.",
    },
    {
      icon: <ShieldCheck size={24} />,
      title: "Verification",
      desc: "Connecting users with verified independent professionals across multiple domains.",
    },
    {
      icon: <Scale size={24} />,
      title: "Independence",
      desc: "Respecting the professional-client relationship and statutory codes of conduct.",
    },
  ];

  return (
    <div className="min-h-screen bg-[#f8fafc] font-outfit relative pb-20">
      
      {/* Dark Hero Background for Navbar Visibility & Premium Look */}
      <div className="absolute top-0 left-0 w-full h-[180px] bg-[#020617] z-0 overflow-hidden rounded-b-[2rem] shadow-xl">
         <div className="absolute inset-0 opacity-20">
            <img src="https://images.unsplash.com/photo-1497215728101-856f4ea42174?auto=format&fit=crop&q=80&w=2000" alt="cover" className="w-full h-full object-cover" />
         </div>
         <div className="absolute inset-0 bg-gradient-to-b from-[#020617]/50 to-[#020617]"></div>
      </div>

      {/* Container */}
      <div className="relative z-10 pt-24 max-w-7xl w-full mx-auto px-4 md:px-6">
        
        {/* Breadcrumbs */}
        <div className="text-white text-xs font-bold uppercase tracking-widest mb-6 opacity-80 flex items-center gap-2">
            <a href="/home" className="hover:text-blue-300 transition-colors">Home</a>
            <span>/</span>
            <span className="text-white opacity-100">About Us</span>
        </div>

        {/* Main Content White Canvas */}
        <div className="bg-white rounded-[2rem] shadow-xl border border-slate-100 p-6 md:p-12 overflow-hidden">
          
          {/* Top Header Section */}
          <div className="flex flex-col items-center text-center mb-16">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="px-4 py-1.5 rounded-full bg-blue-50 border border-blue-100 mb-6"
            >
              <span className="text-xs font-bold text-blue-600 uppercase tracking-[0.2em]">
                Who We Are
              </span>
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-3xl md:text-5xl font-bold text-slate-800 tracking-tight mb-4"
            >
              About <span className="text-blue-600">EasyMarg</span>
            </motion.h1>
            <motion.p 
               initial={{ opacity: 0 }}
               animate={{ opacity: 1 }}
               className="text-sm md:text-lg text-slate-500 font-medium max-w-2xl leading-relaxed"
            >
              Your digital gateway to professional expertise. We bridge the gap between business needs and subject-matter masters.
            </motion.p>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div className="relative p-8 rounded-3xl bg-slate-50 border border-slate-100">
                <h3 className="text-xl font-bold text-slate-800 mb-4">The Aggregator Model</h3>
                <p className="text-sm md:text-base text-slate-600 leading-relaxed">
                  <span className="font-semibold text-slate-700">EasyMarg is a digital aggregator platform</span> that connects individuals, startups, and MSMEs with independent professionals like Advocates, Chartered Accountants, and Financial Advisors.
                </p>
                <div className="mt-6 flex items-center gap-4 p-4 bg-white rounded-2xl border border-slate-100 shadow-sm">
                  <Landmark className="text-blue-400" />
                  <p className="text-xs font-medium text-slate-500 italic">
                    "Strictly focused on facilitating discovery and communication."
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-1 gap-2"
            >
              {objectives.map((obj, i) => (
                <div key={i} className="flex gap-4 p-4 rounded-2xl hover:bg-slate-50 transition-all duration-300 group">
                  <div className="w-12 h-12 shrink-0 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-all duration-300">
                    {obj.icon}
                  </div>
                  <div>
                    <h4 className="text-base font-bold text-slate-800 mb-1">{obj.title}</h4>
                    <p className="text-slate-500 text-xs leading-relaxed">{obj.desc}</p>
                  </div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Disclaimer Section - Updated to Light Blue/Cyan Shade */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-blue-600 to-cyan-500 rounded-3xl p-8 md:p-12 text-center text-white relative overflow-hidden shadow-lg shadow-blue-200"
          >
            <div className="absolute top-0 right-0 p-8 opacity-10">
               <ShieldCheck size={160} />
            </div>
            <h2 className="text-2xl font-bold mb-4">Our Commitment to Integrity</h2>
            <p className="text-white/90 text-sm md:text-base max-w-3xl mx-auto leading-relaxed mb-8">
              EasyMarg does not influence professional advice, outcomes, or opinions. All professionals listed operate independently, governed by their respective statutory bodies and ethical standards.
            </p>
            <div className="flex flex-wrap justify-center gap-3 relative z-10">
               <div className="px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-[10px] font-bold uppercase tracking-widest">Independence</div>
               <div className="px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-[10px] font-bold uppercase tracking-widest">Transparency</div>
               <div className="px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-[10px] font-bold uppercase tracking-widest">Confidentiality</div>
            </div>
          </motion.div>

        </div>
      </div>
    </div>
  );
};

export default AboutSection;